import { Calculation } from "@/services/backend/rlt/calculations/types/calculation";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import t from "@/lang/lang";
import { NavLink, useNavigate, useParams } from "react-router";
import {
  useRltCalculationDeleteMutation,
  useRltCalculationShowQuery,
} from "@/services/backend/rlt/calculations/api/calculation-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useToast } from "@/shared/hooks/use-toast";
import { Button } from "@/shared/components/ui/button";
import { Trash2 } from "lucide-react";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { useEffect } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { ProcessPageMainTemplate } from "../../../_components/process-page-main-template";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { RltBreadcrumbs } from "../../_components/rlt-breadcrumbs";

export function CalculationRoute() {
  const { calculationId } = useParams();
  const {
    data: calculation,
    isLoading,
    error,
  } = useRltCalculationShowQuery({ id: calculationId! });

  const breadcrumbs = calculation && (
    <RltBreadcrumbs
      customerId={calculation.customerId}
      processId={calculation.processId!}
    >
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/rlt/${calculation.processId!}/calculations`}>
            {t("Kalkulationen")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <DocumentNumberById documentId={calculation.id} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </RltBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {calculation && <CalculationPage calculation={calculation} />}
    </ProcessPageMainTemplate>
  );
}

function CalculationPage({ calculation }: { calculation: Calculation }) {
  const links = [
    { to: "treatment", label: t("Aufnahme") },
    { to: "calculation", label: t("Kalkulation") },
    { to: "textblocks", label: t("Bemerkungen") },
  ];

  return (
    <PersistenceContextProvider>
      <DocumentPage>
        <DocumentPageHeader>
          <DocumentPageNavigation>
            {links.map((link) => (
              <DocumentPageNavigationLink to={link.to} key={link.to}>
                {link.label}
              </DocumentPageNavigationLink>
            ))}
          </DocumentPageNavigation>
          <DocumentPageActions>
            <PersistenceStateUsingContext className="mr-2" />
            <DeleteCalculationDialog calculationId={calculation.id} />
          </DocumentPageActions>
        </DocumentPageHeader>
        <DocumentPageContent>
          <DocumentPageNavigationOutlet />
        </DocumentPageContent>
      </DocumentPage>
    </PersistenceContextProvider>
  );
}

function DeleteCalculationDialog(props: { calculationId: string }) {
  const { calculationId } = props;
  const [deleteCalculation, { isLoading, error, isSuccess, reset }] =
    useRltCalculationDeleteMutation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const onClick = () => {
    if (isLoading) {
      return;
    }
    deleteCalculation({ id: calculationId });
  };

  useEffect(() => {
    if (isSuccess) {
      toast({
        title: t("Kalkulation gelöscht"),
        variant: "success",
      });
      navigate(`..`);
      reset();
    }
  }, [error, isSuccess, navigate, reset, toast]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Trash2 className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Kalkulation wirklich löschen?")}</DialogTitle>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={onClick}>
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
